import { useCallback, useState, useRef, useEffect } from 'react';
import useUploadStrikeMutation from '../../hooks/useUploadStrikeMutation';
import { AlertType } from '../../../design-system/DSAlert';
import useAppContext from '../../../hooks/useAppContext';
import { readAudioDurationInSeconds } from '../../../utils/uploads';
import { MAX_STRIKE_DURATION_SECONDS } from '../../../consts/audio';

type UseStrikeUploadProps = {
  initialStrikeUrl?: string;
  initialStrikeName?: string;
  showAlert: (alert: { message: string; alertType: AlertType }) => void;
  onRemove?: () => void;
};

type UseStrikeUploadReturn = {
  strikeUrl: string;
  strikeName: string;
  isLoadingStrike: boolean;
  handleStrikeFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  handleRemoveStrike: () => void;
};

const useStrikeUpload = ({
  initialStrikeUrl = '',
  initialStrikeName = '',
  showAlert,
  onRemove,
}: UseStrikeUploadProps): UseStrikeUploadReturn => {
  const { isAdmin } = useAppContext();
  const [strikeUrl, setStrikeUrl] = useState<string>(initialStrikeUrl);
  const [strikeName, setStrikeName] = useState<string>(initialStrikeName);

  const strikeAbortControllerRef = useRef<AbortController | null>(null);
  const { mutate: uploadStrikeMutation, isPending: isLoadingStrike } =
    useUploadStrikeMutation();

  useEffect(() => {
    return () => {
      if (strikeAbortControllerRef.current) {
        strikeAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handleStrikeFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const file = input.files?.[0];
      if (!file) return;

      const validTypes = ['audio/mp3', 'audio/wave', 'audio/wav', 'audio/mpeg'];

      if (!validTypes.includes(file.type)) {
        showAlert({
          message:
            'Invalid strike tone file type. Please upload MP3 or WAV files.',
          alertType: AlertType.ERROR,
        });
        input.value = '';
        return;
      }

      // undefined means the browser could not measure it; klydo-api rejects
      // over-length files too, so let the upload through and let it decide.
      const duration = await readAudioDurationInSeconds(file);

      if (
        duration !== undefined &&
        duration > MAX_STRIKE_DURATION_SECONDS &&
        !isAdmin
      ) {
        showAlert({
          message: `Strike tone must not be longer than ${MAX_STRIKE_DURATION_SECONDS} seconds. Your file is ${duration.toFixed(1)} seconds.`,
          alertType: AlertType.ERROR,
        });
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        strikeAbortControllerRef.current = new AbortController();
        uploadStrikeMutation(
          {
            file: reader.result as string,
            fileName: file.name,
            fileType: file.type,
          },
          {
            onSuccess: (response) => {
              if (response.status === 'approved' && response.url) {
                setStrikeUrl(response.url);
                setStrikeName(file.name);
              } else {
                showAlert({
                  message: response.error || 'Failed to upload strike tone',
                  alertType: AlertType.ERROR,
                });
                input.value = '';
              }
            },
            onError: (error) => {
              showAlert({
                message:
                  error.response?.data?.message ||
                  'Failed to upload strike tone',
                alertType: AlertType.ERROR,
              });
              input.value = '';
            },
          },
        );
      };
      reader.readAsDataURL(file);
    },
    [showAlert, uploadStrikeMutation, isAdmin],
  );

  const handleRemoveStrike = useCallback(() => {
    if (strikeAbortControllerRef.current) {
      strikeAbortControllerRef.current.abort();
    }
    setStrikeUrl('');
    setStrikeName('');
    onRemove?.();
  }, [onRemove]);

  return {
    strikeUrl,
    strikeName,
    isLoadingStrike,
    handleStrikeFileChange,
    handleRemoveStrike,
  };
};

export default useStrikeUpload;
